import React from 'react';
import {
	FileViewerParams,
	MultipleWaterMarkedResourceViewerParams,
	viewerExtensionSlots,
	WatermarkCheckBoxParams,
	WaterMarkedResourceViewerParams,
} from '@sharefiledev/fileviewer-pilet-sdk';
import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import { Skeleton } from 'antd';
import { setupWatermarkEventService } from './Components/Events/EventHandler';
import { WopiEditViewer } from './Components/FileViewer/DocViewer/WopiEditViewer';
import { DesktopShareModal } from './Components/Share/DesktopShareModal';
import { DESKTOP_SHARE_MODEL_PATH, FILE_EDIT_PATH } from './utils/constants';

export type ExtensionImplementationProps<TParams> = TParams & { piletApi: PiletApi };

const LazyFileViewer = React.lazy(() => import('./FileViewerPilet'));
const AsyncLazyFilesViewer: React.FC<
	ExtensionImplementationProps<FileViewerParams>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyFileViewer {...props} />
		</React.Suspense>
	);
};

const LazyWaterMarkedResourceViewer = React.lazy(
	() => import('./WaterMarkResourceViewerContent')
);
const AsyncLazyWaterMarkedResourceViewer: React.FC<
	ExtensionImplementationProps<WaterMarkedResourceViewerParams>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyWaterMarkedResourceViewer {...props} />
		</React.Suspense>
	);
};

const LazyMultipleWaterMarkedResourceViewer = React.lazy(
	() => import('./MultipleWaterMarkResourceViewerContent')
);
const AsyncLazyMultipleWaterMarkedResourceViewer: React.FC<
	ExtensionImplementationProps<MultipleWaterMarkedResourceViewerParams>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyMultipleWaterMarkedResourceViewer {...props} />
		</React.Suspense>
	);
};

const LazyWaterMarkCheckBox = React.lazy(
	() => import('./Components/WatermarkCheckBoxContent')
);
const AsyncLazyWaterMarkCheckBox: React.FC<
	ExtensionImplementationProps<WatermarkCheckBoxParams>
> = props => {
	return (
		<React.Suspense fallback={<Skeleton />}>
			<LazyWaterMarkCheckBox {...props} />
		</React.Suspense>
	);
};

export function setup(app: PiletApi) {
	const watermarkEventService = setupWatermarkEventService(app);

	// Store service reference for disposal management
	app.setData('watermarkEventService', watermarkEventService);

	app.registerExtension(viewerExtensionSlots.FileViewer, ({ params }) => (
		<AsyncLazyFilesViewer piletApi={app} {...params}></AsyncLazyFilesViewer>
	));

	app.registerPage(`${FILE_EDIT_PATH}/:fileId`, () => <WopiEditViewer piletApi={app} />, {
		pageType: 'blankPage',
	});

	app.registerPage(
		`${DESKTOP_SHARE_MODEL_PATH}/:versionAgnosticId`,
		() => <DesktopShareModal piletApi={app} />,
		{
			pageType: 'fullPage',
		}
	);

	app.registerExtension(viewerExtensionSlots.WaterMarkedResourceViewer, ({ params }) => (
		<AsyncLazyWaterMarkedResourceViewer
			piletApi={app}
			{...params}
		></AsyncLazyWaterMarkedResourceViewer>
	));

	app.registerExtension(
		viewerExtensionSlots.MultipleWaterMarkedResourceViewer,
		({ params }) => (
			<AsyncLazyMultipleWaterMarkedResourceViewer
				piletApi={app}
				{...params}
			></AsyncLazyMultipleWaterMarkedResourceViewer>
		)
	);

	app.registerExtension(viewerExtensionSlots.WatermarkCheckBox, ({ params }) => (
		<AsyncLazyWaterMarkCheckBox piletApi={app} {...params}></AsyncLazyWaterMarkCheckBox>
	));
}
